import React, { useState, useEffect } from "react";
import "./StatusBar.css";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { setDoc, doc, getDoc, getDocs, collection } from "firebase/firestore";
import { auth, db, storage } from "../../firebase";
import FullScreenStatus from "./FullScreenStatus";
import { addStatusNotification } from "../../utils/notifications";
import { v4 as uuidv4 } from "uuid";

const StatusBar = ({ initialStatuses }) => {
  const [statuses, setStatuses] = useState(initialStatuses || []);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (err) {
          console.error("Failed to fetch user data:", err);
        }
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const snapshot = await getDocs(collection(db, "statuses"));
        const statusArray = snapshot.docs.map((doc) => doc.data());
        setStatuses(statusArray);
      } catch (error) {
        console.error("Error fetching statuses:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatuses();
  }, []);

  const handleStatusUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const user = auth.currentUser;
    if (!user) return;

    setUploadProgress(0);

    try {
      const fileId = uuidv4();
      const storageRef = ref(
        storage,
        `statuses/${user.uid}/${fileId}_${file.name}`,
      );
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
          );
          // Cap it at 99% during the actual upload to keep the "Processing" logic clean
          setUploadProgress(progress === 100 ? 99 : progress);
        },
        (error) => {
          console.error(error);
          setUploadProgress(null);
        },
        async () => {
          // Change UI to 100 or "Processing" immediately
          setUploadProgress(100);

          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            const newStatus = {
              id: fileId,
              uid: user.uid,
              username:
                userData?.firstName || user.displayName || "Unknown User",
              profilePicture: userData?.profilePicture || user.photoURL || "",
              statusPicture: downloadURL,
              timestamp: new Date().toISOString(),
              views: 0,
            };

            await Promise.all([
              setDoc(doc(db, "statuses", fileId), newStatus),
              addStatusNotification(newStatus).catch((e) =>
                console.log("Notification delayed", e),
              ),
            ]);

            setStatuses((prev) => [newStatus, ...prev]);
          } catch (dbError) {
            console.error("Database error:", dbError);
          } finally {
            setUploadProgress(null);
            event.target.value = null;
          }
        },
      );
    } catch (error) {
      setUploadProgress(null);
    }
  };

  return (
    <>
      <div className="statusBar">
        <div className="toto">
          <div className="statusItem">
            <div className="statusUploadWrapper">
              {uploadProgress !== null && (
                <svg className="progressCircle" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="48"
                    stroke="transparent"
                    strokeWidth="4"
                    fill="none"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="48"
                    stroke="#ff0000" // Change this to your preferred brand color
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray="301.59" // Circumference (2 * pi * r)
                    strokeDashoffset={301.59 - (301.59 * uploadProgress) / 100}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 0.3s ease" }}
                  />
                </svg>
              )}

              <div
                className={`statusUpload ${uploadProgress !== null ? "isUploading" : ""}`}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleStatusUpload}
                  className="statusInput"
                />
                <span className="statusUploadText">
                  {uploadProgress !== null
                    ? uploadProgress === 100
                      ? "..."
                      : `${uploadProgress}%`
                    : "+"}
                </span>
              </div>
            </div>
            <div className="statusnameee">Your story</div>
          </div>
        </div>

        {loading ? (
          <div>Loading statuses...</div>
        ) : statuses.length === 0 ? (
          <div>No statuses yet.</div>
        ) : (
          statuses.map((status) => (
            <div
              key={status.id}
              className="statusItem"
              onClick={() => setSelectedStatus(status)}
            >
              <img
                src={status.statusPicture}
                alt="Status"
                className="statusImage"
              />
              <div className="statusnameee">{status.username}</div>
            </div>
          ))
        )}

        {selectedStatus && (
          <FullScreenStatus
            status={selectedStatus}
            onClose={() => setSelectedStatus(null)}
          />
        )}
      </div>
    </>
  );
};

export default StatusBar;
